import { useMemo, useEffect } from "react";
import { ChartView } from "../chartView/ChartView";

import { useNavigate, useLocation } from "react-router";
import { ChartViewORM } from "../chartViewORM/components/ChartViewORM";

import type {
  ChartConfigPayloadED,
  ChartConfigPayloadIC,
  ChartConfigPayloadORM
} from "@certa/types";
import { ChartViewED } from "../chartViewED/components/ChartViewED";
import { ChartViewIC } from "../chartViewIC/components/ChartViewIC";
import type { ReportTableType } from "@certa/common/constants";
import type { ChartIdentifiers, ChartConfig } from "@certa/common/types";
import type {
  AggregateResponse,
  ProcessCycleChartAPIResponseType
} from "@certa/common/modelCreators";
import { chartConfigModelCreator } from "@certa/common/modelCreators";
import { decodeUrlSafeB64 } from "@certa/common/utils/helper";

type Query = {
  data: AggregateResponse | ProcessCycleChartAPIResponseType;
  error?: string;
  reportType?: ReportTableType;
  graphConfig: {
    chartType: ChartIdentifiers;
    chartConfig:
      | ChartConfig
      | ChartConfigPayloadORM
      | ChartConfigPayloadED
      | ChartConfigPayloadIC;
  };
};

const useQuery = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = useMemo(() => {
    const query = new URLSearchParams(search);
    const encodedString = query.get("data");
    try {
      const decodedString = decodeUrlSafeB64(encodedString || "");
      if (decodedString) {
        return JSON.parse(decodedString) as Query;
      }
    } catch (error) {
      console.error(error);
    }
  }, [search]);

  useEffect(() => {
    if (!query) {
      // redirect to home page if query is not present
      // and if user is not authenticated then it will redirect to login page
      navigate("/", { replace: true });
    }
  }, [navigate, query]);

  return query;
};

const ChartExport = () => {
  const query = useQuery();
  const reportType = query?.reportType || "Workflow Report";
  const chartType = query?.graphConfig.chartType ?? "LINE_CHART";
  const { chartConfig, chartConfigED, chartConfigIC, chartConfigORM } =
    chartConfigModelCreator(reportType, query?.graphConfig.chartConfig);

  if (!query) {
    return null;
  } else if (reportType === "ORM Workflow Report") {
    return (
      <ChartViewORM
        apiData={query.data as AggregateResponse}
        chartType={chartType}
        chartConfigORM={chartConfigORM}
        isLoading={false}
        error={query.error}
        isAnimationActive={false}
      />
    );
  } else if (reportType === "Integration Call Report") {
    if (!chartConfigIC) {
      return null;
    }
    return (
      <ChartViewIC
        apiData={query.data as AggregateResponse}
        chartType={chartType}
        chartConfigIC={chartConfigIC}
        isLoading={false}
        error={query.error}
        isAnimationActive={false}
      />
    );
  } else if (reportType === "Email Delivery Report") {
    if (!chartConfigED) {
      return null;
    }
    return (
      <ChartViewED
        apiData={query.data as AggregateResponse}
        chartType={chartType}
        chartConfigED={chartConfigED}
        isLoading={false}
        error={query.error}
        isAnimationActive={false}
      />
    );
  }

  return (
    <ChartView
      apiData={query.data}
      chartType={chartType}
      chartConfig={chartConfig}
      isLoading={false}
      error={query.error}
      isAnimationActive={false}
    />
  );
};
/**
 * NOTE: This is a lazy component and requires default export
 */
// eslint-disable-next-line import/no-default-export
export default ChartExport;
